import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import Modal from "../../../components/Modal"
import Button from "../../../components/Button"
import Switch from "../../../components/Switch"
import LoaderDots from "../../../components/LoaderDots"
import WhiteSpace from "../../../components/Whitespace"
import Icon from "../../../Icon"
import { useNotification } from "../../../modules/Notification"
import { IconButton } from "../../Users/userStyles"
import { ERR_OK_RESTART_NEEDED, isGeneralOk, sendChargerCommand } from "./chargerCommand"
import {
  ButtonRow, CenteredLoader, CloseButtonContainer, CollapseHeader, Content, Divider, HelpText, Layout, Legend, LegendModifiable,
  LegendModifiedSwatch, MutedMessage, PanelTitle, ParamCard, ParamGrid, ParamInput, ParamKey, ParamValueText, Placeholder,
  PlaceholderTitle, ResultRow, Row, RowLabel, SearchInput, Section, Segment, SegmentedControl, StatusBadge,
} from "./integrationStyles"

// The apps' OCPP screen (OcppView on iOS, OCPPConfigFragment on Android), VLT-2695: an "OCPP enabled"
// switch with the OCPP access mode and the two keys everybody needs up top, and every configuration
// key the charger reports under Advanced. msg 12 gives the access mode, msg 37 the keys; saving sends
// msg 65 for the mode and msg 39 for the changed keys, then shows the charger's verdict per key.

interface OcppKey { key: string, readonly: boolean, value: string }
interface EditableKey extends OcppKey { original: string }
interface SaveResult { key: string, value: string, result?: string }

// Charger access modes (BCMD_CHG_ACCESS_MODE)
const CAM_HOME = 0
const CAM_HOME_RFID = 1
const CAM_HOME_RFID_OCPP = 2
const CAM_OCPP_RFID = 3
const CAM_OCPP_FREE = 4

const OCPP_MODES = [
  { value: CAM_HOME_RFID_OCPP, labelKey: 'charger_homeRfidOcpp' },
  { value: CAM_OCPP_RFID, labelKey: 'charger_rfidOcpp' },
  { value: CAM_OCPP_FREE, labelKey: 'charger_freeOcpp' },
]

const MAIN_KEYS = ['OCPPBackendJSONURL', 'ChargeBoxIdentity']

const RESULT_LABELS: { [result: string]: { key: string, tone: 'success' | 'danger' | 'warning' } } = {
  Accepted: { key: 'charger_accepted', tone: 'success' },
  Rejected: { key: 'charger_refused', tone: 'danger' },
  RebootRequired: { key: 'charger_rebootRequired', tone: 'warning' },
  NotSupported: { key: 'charger_notSupported', tone: 'danger' },
}

const SOFTWARE_RESTART = { msg_type: 15, sub_cmd: 12 }

const isOcppMode = (mode: number) => mode === CAM_HOME_RFID_OCPP || mode === CAM_OCPP_RFID || mode === CAM_OCPP_FREE

// What the switch amounts to. Leaving OCPP goes back to home charging and keeps RFID where the OCPP mode
// used it, as the iOS app does. Anything else stays exactly as the charger reported it: both apps
// rewrite the mode on every save, which quietly turns a prepaid-RFID charger into a home RFID one.
function targetMode(original: number, ocppEnabled: boolean, selected: number): number {
  if (ocppEnabled) return selected
  if (!isOcppMode(original)) return original
  return original === CAM_OCPP_FREE ? CAM_HOME : CAM_HOME_RFID
}

function KeyField({ item, onChange }: { item: EditableKey, onChange: (key: string, value: string) => void }) {
  return (
    <ParamCard $isModified={item.value !== item.original}>
      <ParamKey $isReadonly={item.readonly}>{item.key}</ParamKey>
      {item.readonly
        ? <ParamValueText>{item.value || '-'}</ParamValueText>
        : <ParamInput aria-label={item.key} value={item.value} spellCheck={false} autoComplete="off" onChange={e => onChange(item.key, e.target.value)} />}
    </ParamCard>
  )
}

export default function OcppModal({ chargerId, onClose }: { chargerId: string, onClose: () => void }) {
  const { t } = useTranslation()
  const { addNotification } = useNotification()

  const [originalMode, setOriginalMode] = useState<number | null>()
  const [ocppEnabled, setOcppEnabled] = useState<boolean>(false)
  const [selectedMode, setSelectedMode] = useState<number>(CAM_OCPP_FREE)
  const [keys, setKeys] = useState<EditableKey[] | null>()
  const [search, setSearch] = useState<string>('')
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false)
  const [results, setResults] = useState<SaveResult[] | null>(null)
  const [isSaving, setIsSaving] = useState<boolean>(false)

  // Swapping between the form and the results unmounts the focused button, and the shared Modal only
  // hears Escape from inside itself, so focus goes back onto the dialog after each swap.
  const layoutRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    layoutRef.current?.focus()
  }, [results])

  function applyMode(mode: number) {
    setOriginalMode(mode)
    setOcppEnabled(isOcppMode(mode))
    setSelectedMode(isOcppMode(mode) ? mode : CAM_OCPP_FREE)
  }

  async function fetchAccessMode() {
    try {
      const info: { access_mode?: number } = await sendChargerCommand(chargerId, { msg_type: 12 })
      if (typeof info.access_mode !== 'number') throw new Error('no access_mode')
      applyMode(info.access_mode)
    } catch (err) {
      console.log('Unable to read the access mode', err)
      setOriginalMode(null)
    }
  }

  async function fetchKeys() {
    try {
      const reply: { configurationKey?: OcppKey[] } = await sendChargerCommand(chargerId, { msg_type: 37 })
      setKeys((reply.configurationKey ?? [])
        .map(item => ({ key: item.key, readonly: item.readonly, value: String(item.value ?? ''), original: String(item.value ?? '') }))
        .sort((a, b) => a.key.localeCompare(b.key)))
    } catch (err) {
      console.log('Unable to read the OCPP configuration', err)
      setKeys(null)
    }
  }

  useEffect(() => {
    fetchAccessMode()
    fetchKeys()
  }, [chargerId])

  function changeKey(key: string, value: string) {
    setKeys(prev => prev?.map(item => item.key === key ? { ...item, value } : item))
  }

  const target = originalMode == null ? null : targetMode(originalMode, ocppEnabled, selectedMode)
  const changedKeys = (keys ?? []).filter(item => item.value !== item.original)
  const isModeChanged = target != null && target !== originalMode
  const hasChanges = isModeChanged || changedKeys.length > 0

  async function save() {
    if (!hasChanges || isSaving || target == null) return
    setIsSaving(true)
    const saved: SaveResult[] = []
    try {
      if (isModeChanged) {
        const reply = await sendChargerCommand(chargerId, { msg_type: 65, access_mode: target })
        if (!isGeneralOk(reply)) throw new Error(`access mode refused (${reply?.err_code})`)
        const label = OCPP_MODES.find(mode => mode.value === target)?.labelKey
        // "OK, restart needed" means the new mode applies after a restart, the same as a key that needs one.
        saved.push({ key: t('charger_mode'), value: label ? t(label) : t('charger_off'), result: reply.err_code === ERR_OK_RESTART_NEEDED ? 'RebootRequired' : 'Accepted' })
        setOriginalMode(target)
      }
      if (changedKeys.length > 0) {
        const reply: { results?: string[] } = await sendChargerCommand(chargerId, { msg_type: 39, params: changedKeys.map(item => ({ key: item.key, value: item.value })) })
        // The results come back in the order the keys were sent.
        changedKeys.forEach((item, index) => saved.push({ key: item.key, value: item.value, result: reply.results?.[index] }))
      }
      setResults(saved)
    } catch (err) {
      console.log('Unable to save the OCPP settings', err)
      addNotification(t('charger_couldNotSaveTheSettingsOnTheCharger'), 'TEXT', 'error')
      if (saved.length > 0) setResults(saved)
    }
    setIsSaving(false)
  }

  function closeResults() {
    setResults(null)
    setKeys(undefined)
    fetchKeys()
  }

  async function rebootAndClose() {
    try {
      await sendChargerCommand(chargerId, SOFTWARE_RESTART)
    } catch (err) {
      console.log('Unable to restart the charger', err)
      addNotification(t('charger_couldNotSaveTheSettingsOnTheCharger'), 'TEXT', 'error')
    }
    onClose()
  }

  const query = search.trim().toLowerCase()
  const visibleKeys = (keys ?? []).filter(item => !query || item.key.toLowerCase().includes(query) || item.value.toLowerCase().includes(query))
  const mainKeys = MAIN_KEYS.map(name => keys?.find(item => item.key === name)).filter((item): item is EditableKey => !!item)
  const isLoading = originalMode === undefined || keys === undefined
  const isRebootRequired = !!results?.some(item => item.result === 'RebootRequired')

  return (
    <Modal onEscape={onClose}>
      <Layout ref={layoutRef} tabIndex={-1} style={{ outline: 'none' }} role="dialog" aria-modal="true" aria-label={t('charger_ocppSettings')}>
        <Content>
          <CloseButtonContainer>
            <IconButton aria-label={t('charger_close')} onClick={onClose}>
              <Icon path="general.x" width="20px" height="20px" />
            </IconButton>
          </CloseButtonContainer>

          {results ? <>
            <PanelTitle>{t('charger_ocppResults')}</PanelTitle>
            <Section>
              {results.map(item => {
                const label = item.result ? RESULT_LABELS[item.result] : undefined
                return (
                  <ResultRow key={item.key}>
                    <div>
                      <ParamKey $isReadonly>{item.key}</ParamKey>
                      <ParamValueText>{item.value || '-'}</ParamValueText>
                    </div>
                    {label ? <StatusBadge $tone={label.tone}>{t(label.key)}</StatusBadge> : <StatusBadge $tone="neutral">{item.result ?? '-'}</StatusBadge>}
                  </ResultRow>
                )
              })}
            </Section>
            <ButtonRow>
              <Button label={t('charger_close')} width="120px" onclick={closeResults} bgcolor="transparent" labelcolor="black" />
              {isRebootRequired && <Button label={t('charger_rebootDevice')} width="160px" onclick={rebootAndClose} />}
            </ButtonRow>
          </> : <>
            <PanelTitle>{t('charger_ocppSettings')}</PanelTitle>
            {isLoading && <CenteredLoader><LoaderDots /></CenteredLoader>}
            {!isLoading && (originalMode === null || keys === null) && <Section><MutedMessage>{t('charger_couldNotLoadTheSettingsFromTheCharger')}</MutedMessage></Section>}
            {!isLoading && originalMode != null && keys && <>
              <Section>
                <Row>
                  <RowLabel>{t('charger_ocppEnabled')}</RowLabel>
                  <Switch ariaLabel={t('charger_ocppEnabled')} toggled={ocppEnabled} setToggled={setOcppEnabled} />
                </Row>
                {ocppEnabled && <>
                  <Divider />
                  <Row>
                    <RowLabel>{t('charger_mode')}</RowLabel>
                    <SegmentedControl role="radiogroup" aria-label={t('charger_mode')}>
                      {OCPP_MODES.map(mode => (
                        <Segment key={mode.value} role="radio" aria-checked={selectedMode === mode.value} $selected={selectedMode === mode.value} onClick={() => setSelectedMode(mode.value)}>
                          {t(mode.labelKey)}
                        </Segment>
                      ))}
                    </SegmentedControl>
                  </Row>
                  {mainKeys.length > 0 && <>
                    <WhiteSpace height="12px" />
                    <ParamGrid>
                      {mainKeys.map(item => <KeyField key={item.key} item={item} onChange={changeKey} />)}
                    </ParamGrid>
                  </>}
                </>}
              </Section>

              <Section>
                <CollapseHeader aria-expanded={isAdvancedOpen} onClick={() => setIsAdvancedOpen(open => !open)}>
                  {t('charger_advancedSettings')}
                  <span style={{ display: 'inline-flex', transform: isAdvancedOpen ? 'rotate(180deg)' : 'none' }}>
                    <Icon path="general.downArrow" width="16px" height="16px" />
                  </span>
                </CollapseHeader>
                {isAdvancedOpen && (keys.length === 0
                  ? <Placeholder>
                    <PlaceholderTitle>{t('charger_noOcppParameterAvailable')}</PlaceholderTitle>
                    <HelpText>{t('charger_youCanFindAndSetTheChargerOcppParametersOnlyUseThisIfYouKnowWhatYouAreDoing')}</HelpText>
                  </Placeholder>
                  : <>
                    <WhiteSpace height="16px" />
                    <SearchInput aria-label={t('charger_whatAreYouLookingFor')} placeholder={t('charger_whatAreYouLookingFor')} value={search} onChange={e => setSearch(e.target.value)} />
                    <WhiteSpace height="12px" />
                    <Legend>
                      <span><LegendModifiable />{t('charger_modifiable')}</span>
                      <span><LegendModifiedSwatch />{t('charger_modified')}</span>
                    </Legend>
                    <WhiteSpace height="12px" />
                    <ParamGrid>
                      {visibleKeys.map(item => <KeyField key={item.key} item={item} onChange={changeKey} />)}
                    </ParamGrid>
                  </>)}
              </Section>

              <ButtonRow style={{ opacity: hasChanges ? 1 : 0.5, pointerEvents: hasChanges ? 'auto' : 'none' }}>
                <Button label={isSaving ? <LoaderDots /> : t('charger_save')} width="120px" onclick={save} />
              </ButtonRow>
            </>}
          </>}
        </Content>
      </Layout>
    </Modal>
  )
}
